import { MutableRefObject, PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {
  useDomains,
  useHostingAccount,
  useHostingAdvancedDomainServicesCheck,
} from '@newfold/huapi-js';

import CTB from '~/components/CTB';
import useAccount from '~/hooks/useAccount';
import { useBrandInfo } from '~/hooks/useBrandInfo';
import useDataCenter from '~/hooks/useDataCenter';
import useFeature from '~/hooks/useFeature';

import Sites from '../Sites';
import AdvancedHostingDiskUsageStats from './components/DiskUsage';
import AdvancedHostingServerInfo from './components/Server/ServerInfo';
import AdvancedHostingServerStatus from './components/Server/ServerStatus';
import Wrapper from './components/Server/Wrapper';

const AdvancedHostingOverView = ({
  container = null,
  hasPurchasedRecently = false,
}: PropsWithChildren<{
  container?: MutableRefObject<HTMLElement | null> | null;
  hasPurchasedRecently?: boolean;
}>) => {
  const { t } = useTranslation('hosting');

  const { id: hostingId } = useAccount();
  const { isDreamscape } = useBrandInfo();
  const [isUpgradeCTBAllowed, setIsUpgradeCTBAllowed] = useState(false);

  const [showServerControls] = useFeature('server_controls');
  const [canUpgrade] = useFeature('hosting_upgrade');
  const [hasSites] = useFeature('sites');
  const [, account] = useFeature('advanced_hosting');

  const { data: hostingAccount } = useHostingAccount(hostingId, {
    query: {
      enabled: !!hostingId,
    },
  });
  const hostingName = hostingAccount?.data?.type;
  const { dCType } = useDataCenter();
  const hostingType = dCType;

  // TODO: replace with hostingDomainsV2
  const { data } = useDomains(hostingId);
  const hasDomains = !!data?.data?.domains && data?.data?.domains?.length > 0;

  const ACCOUNT_TYPES = [
    'dedicated_windows',
    'dedicated_plesk',
    'shared_plesk',
    'vps_plesk',
    'vps_windows',
  ];
  const isAccountTypeNotSupported = !ACCOUNT_TYPES.includes(
    account?.accountType,
  );

  const { data: domainServiceData } = useHostingAdvancedDomainServicesCheck(
    hostingId,
    {
      query: {
        enabled: !!hostingId && !!isAccountTypeNotSupported,
      },
    },
  );
  const canAccessEmail =
    !!domainServiceData?.data?.managed_cpanel_user_exists || hasDomains;
  const isDomainServiceConfigured =
    !domainServiceData?.data?.configure_domain_services;

  // Callback to monitor server status changes
  const checkServerStatus = (status: string) => {
    setIsUpgradeCTBAllowed(status === 'active');
  };
  return (
    <Wrapper container={container} hasPurchasedRecently={hasPurchasedRecently}>
      <Card>
        <CardContent>
          <Stack direction="column" spacing={5}>
            {!isDreamscape && (
              <Stack spacing={1}>
                <Typography variant="h2">{t('hosting:planTitle')}</Typography>
                <Stack direction={'row'} spacing={0.5}>
                  <Typography variant="body2">
                    {hostingAccount?.data?.billing?.product_name}
                  </Typography>
                  {canUpgrade && (
                    <CTB
                      product="HOSTING_UPGRADE"
                      disabled={!isUpgradeCTBAllowed}
                      data={{
                        hostingType: hostingType,
                        hostingName: hostingName,
                      }}
                    >
                      {/* @ts-ignore */}
                      <Typography variant="body2" noWrap>
                        {t('upgradeHosting')}
                      </Typography>
                    </CTB>
                  )}
                </Stack>
              </Stack>
            )}

            {showServerControls && (
              <>
                <Stack spacing={1}>
                  <AdvancedHostingServerStatus
                    handleServerStatusChange={checkServerStatus}
                  />
                </Stack>
                <Stack spacing={3}>
                  <AdvancedHostingServerInfo
                    isDomainServiceConfigured={isDomainServiceConfigured}
                  />
                  <Stack>
                    <AdvancedHostingDiskUsageStats
                      canAccessEmail={canAccessEmail}
                    />
                  </Stack>
                </Stack>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* show site list if capability is enabled */}
      {hasSites && <Sites />}
    </Wrapper>
  );
};

export default AdvancedHostingOverView;
